/**
 * NPCInteractionUIManager - Orchestrates all NPC interaction managers
 * Main entry point for NPC interactions
 */

import { NPC } from '../../../shared/NPCData';
import { COLORS } from '../theme';
import { DialogManager } from './dialog/DialogManager';
import { ShopManager } from './shop/ShopManager';
import { QuestOfferManager } from './quest/QuestOfferManager';
import { ServiceManager, type ServiceAction } from './service/ServiceManager';

export interface NPCInteractionConfig {
  npc: NPC;
  playerLevel: number;
  playerGold?: number;
  callbacks?: {
    onDialogSelect?: (option: any) => void;
    onShopPurchase?: (purchase: any) => void;
    onQuestAccept?: (quest: any) => void;
    onQuestComplete?: (quest: any) => void;
    onServiceUse?: (action: ServiceAction) => void;
  };
}

export class NPCInteractionUIManager {
  // Sub-managers
  public dialog: DialogManager;
  public shop: ShopManager | null = null;
  public quests: QuestOfferManager | null = null;
  public services: ServiceManager | null = null;
  
  private npc: NPC;
  
  constructor(config: NPCInteractionConfig) {
    const { npc, playerLevel, callbacks } = config;
    
    this.npc = npc;
    
    // Initialize managers based on NPC capabilities
    this.dialog = new DialogManager(npc, callbacks?.onDialogSelect);
    
    if (npc.shopItems && npc.shopItems.length > 0) {
      this.shop = new ShopManager(npc, callbacks?.onShopPurchase);
    }
    
    if (npc.quests && npc.quests.length > 0) {
      this.quests = new QuestOfferManager(
        npc, 
        playerLevel, 
        callbacks?.onQuestAccept, 
        callbacks?.onQuestComplete
      );
    }
    
    if (npc.services && npc.services.length > 0) {
      this.services = new ServiceManager(npc, callbacks?.onServiceUse);
    }
  }
  
  /**
   * Get active managers for this NPC
   */
  getActiveManagers(): string[] {
    const managers: string[] = ['dialog'];
    if (this.shop) managers.push('shop');
    if (this.quests) managers.push('quests');
    if (this.services) managers.push('services');
    return managers;
  }
  
  /**
   * Update player level (affects quest availability)
   */
  updatePlayerLevel(level: number): void {
    if (this.quests) {
      this.quests.updatePlayerLevel(level);
    }
  }
  
  /**
   * Check if this NPC has any interactions available
   */
  hasInteractions(): boolean {
    return this.getActiveManagers().length > 1; // More than just dialog
  }
  
  /**
   * Get NPC type information
   */
  getNPCTypeInfo() {
    // Bespoke hues (quest/shop/banker/transport) aren't in the semantic palette,
    // so they stay literal. The three that map EXACTLY to a token are routed
    // through it (a visual no-op): healer→danger, trainer→textAccent, story→brand.
    const colors: Record<string, string> = {
      'quest': '#00a8ff',
      'shop': '#00ff88',
      'healer': COLORS.danger,
      'trainer': COLORS.textAccent,
      'banker': '#5f27cd',
      'story': COLORS.brand,
      'transport': '#00d2d3'
    };
    
    const icons: Record<string, string> = {
      'quest': '📜',
      'shop': '🏪',
      'healer': '❤️',
      'trainer': '⚔️',
      'banker': '💰',
      'story': '📖',
      'transport': '🚀'
    };
    
    const names: Record<string, string> = {
      'quest': 'Quest Giver',
      'shop': 'Shopkeeper',
      'healer': 'Healer',
      'trainer': 'Trainer',
      'banker': 'Banker',
      'story': 'Story NPC',
      'transport': 'Transport'
    };
    
    return {
      color: colors[this.npc.type] || COLORS.brand,
      icon: icons[this.npc.type] || '👤',
      name: names[this.npc.type] || 'NPC'
    };
  }
}
